import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, ArrowDownCircle, ArrowUpCircle,
  Loader2, Building2, Send, CheckCircle2, AlertCircle, RefreshCw, Smartphone
} from "lucide-react";
import bkashLogo from "@/assets/bkash-logo.webp";
import nagadLogo from "@/assets/nagad-logo.webp";
import rocketLogo from "@/assets/rocket-logo.webp";
import walletCardImage from "../../attached_assets/file_00000000a2d88208b44a27972f7089da_1788609852240.png";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

type WithdrawMethod = "bkash" | "nagad" | "rocket" | "bank";

interface WalletData {
  balance: number;
  total_earned: number;
  total_withdrawn: number;
}

interface Transaction {
  id: string;
  amount: number;
  type: "credit" | "debit";
  description: string;
  created_at: string;
}

interface WithdrawalRequest {
  id: string;
  amount: number;
  method: string;
  status: string;
  created_at: string;
}

const methodIcons: Record<WithdrawMethod, { label: string; labelBn: string; color: string; bg: string }> = {
  bkash:  { label: "bKash",  labelBn: "বিকাশ",  color: "text-pink-600", bg: "bg-pink-50 border-pink-200" },
  nagad:  { label: "Nagad",  labelBn: "নগদ",    color: "text-orange-600", bg: "bg-orange-50 border-orange-200" },
  rocket: { label: "Rocket", labelBn: "রকেট",   color: "text-purple-600", bg: "bg-purple-50 border-purple-200" },
  bank:   { label: "Bank Transfer", labelBn: "ব্যাংক ট্রান্সফার", color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
};

const WalletPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const bn = language === "bn";

  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"overview" | "withdraw" | "history">("overview");

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState<WithdrawMethod | null>(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawDone, setWithdrawDone] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);

    const [{ data: walletData }, { data: txData }, { data: wdData }] = await Promise.all([
      supabase.from("wallets").select("*").eq("user_id", user.id).maybeSingle(),
      supabase.from("wallet_transactions").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(50),
      supabase.from("withdrawal_requests").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(20),
    ]);

    setWallet(walletData ?? { balance: 0, total_earned: 0, total_withdrawn: 0 });
    setTransactions((txData ?? []) as Transaction[]);
    setWithdrawals((wdData ?? []) as WithdrawalRequest[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const handleWithdraw = async () => {
    if (!user || !withdrawMethod || !accountNumber) return;
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(bn ? "সঠিক পরিমাণ দিন" : "Enter a valid amount");
      return;
    }
    if (wallet && amount > wallet.balance) {
      toast.error(bn ? "অপর্যাপ্ত ব্যালেন্স" : "Insufficient balance");
      return;
    }
    if (amount < 100) {
      toast.error(bn ? "সর্বনিম্ন উত্তোলন ৳100" : "Minimum withdrawal is ৳100");
      return;
    }

    setWithdrawing(true);
    try {
      const { data: success, error } = await supabase.rpc("debit_wallet", {
        _user_id: user.id,
        _amount: amount,
        _description: `Withdrawal via ${methodIcons[withdrawMethod].label}`,
      });

      if (error || !success) {
        toast.error(bn ? "উত্তোলন ব্যর্থ হয়েছে" : "Withdrawal failed. Try again.");
        setWithdrawing(false);
        return;
      }

      const { error: withdrawalError } = await supabase.from("withdrawal_requests").insert({
        user_id: user.id,
        amount,
        method: withdrawMethod,
        account_number: accountNumber,
        bank_name: withdrawMethod === "bank" ? bankName : null,
        status: "pending",
      });
      if (withdrawalError) {
        toast.error(withdrawalError.message);
        // The balance was already debited by debit_wallet; surface the failure
        // instead of claiming that a withdrawal request exists.
        await load();
        setWithdrawing(false);
        return;
      }

      setWithdrawDone(true);
      await load();
    } catch {
      toast.error(bn ? "উত্তোলন ব্যর্থ হয়েছে" : "Withdrawal failed");
    }
    setWithdrawing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const balance = wallet?.balance ?? 0;
  const totalEarned = wallet?.total_earned ?? 0;
  const totalWithdrawn = wallet?.total_withdrawn ?? 0;

  return (
    <motion.div className="mx-auto max-w-2xl space-y-3 text-[#203f3c]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <section className="relative aspect-[2/1] overflow-hidden rounded-[22px]">
        <img src={walletCardImage} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-x-[9%] top-[8%] bottom-[12%] z-10 flex flex-col px-3 py-2 sm:px-5 sm:py-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-white/90 sm:text-sm">{bn ? "সেবাকো ওয়ালেট" : "Sebaco Wallet"}</p>
              <p className="mt-0.5 text-[10px] font-medium tracking-[0.18em] text-white/75 sm:text-xs">•••• •••• •••• 5678</p>
            </div>
            <span className="text-lg font-black italic tracking-tight text-white sm:text-xl">VISA</span>
          </div>
          <div className="mt-auto">
            <p className="text-[10px] font-medium text-white/80 sm:text-xs">{bn ? "মোট ব্যালেন্স" : "Total Balance"}</p>
            <p className="mt-0.5 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">৳{balance.toLocaleString()}</p>
          </div>
        </div>
      </section>

      <section className="rounded-[20px] bg-white p-3 sm:p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-serif text-sm font-extrabold text-[#203f3c]">{bn ? "ব্যালেন্সের সারাংশ" : "Balance Overview"}</h2>
          <button onClick={load} className="flex h-7 w-7 items-center justify-center rounded-full text-[#32877d] hover:bg-[#e8f4f0]" title={bn ? "রিফ্রেশ করুন" : "Refresh balance"}>
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-[16px] bg-[#f8fbfa] p-2.5 sm:p-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e4f3ef] text-[#398b82]"><ArrowDownCircle className="h-3.5 w-3.5" /></span>
              <p className="text-[10px] font-medium text-[#566966]">{bn ? "মোট আয়" : "Total Earned"}</p>
            </div>
            <p className="mt-1 text-lg font-extrabold text-[#203f3c]">৳{totalEarned.toLocaleString()}</p>
            <p className="mt-0.5 text-[8px] text-[#7b8986]">{bn ? "পরামর্শ ফি-এর ৮০%" : "80% of consultation fees"}</p>
          </div>
          <div className="rounded-[16px] bg-[#f8fbfa] p-2.5 sm:p-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e4f3ef] text-[#398b82]"><ArrowUpCircle className="h-3.5 w-3.5" /></span>
              <p className="text-[10px] font-medium text-[#566966]">{bn ? "মোট উত্তোলন" : "Total Withdrawn"}</p>
            </div>
            <p className="mt-1 text-lg font-extrabold text-[#203f3c]">৳{totalWithdrawn.toLocaleString()}</p>
            <p className="mt-0.5 text-[8px] text-[#7b8986]">{bn ? "সফলভাবে উত্তোলিত" : "Successfully withdrawn"}</p>
          </div>
        </div>
      </section>

      <div className="flex gap-1 rounded-[20px] bg-white p-1">
        {(["overview", "withdraw", "history"] as const).map(t => (
          <button key={t} onClick={() => { setTab(t); setWithdrawDone(false); }}
            className={`flex-1 rounded-full py-2 text-[11px] font-bold transition-colors ${tab === t ? "bg-[#237e76] text-white" : "text-[#273f3d] hover:bg-[#eef7f4]"}`}>
            {t === "overview" ? (bn ? "কার্যক্রম" : "Activity")
              : t === "withdraw" ? (bn ? "উত্তোলন" : "Withdraw")
              : (bn ? "ইতিহাস" : "History")}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === "overview" && (
          <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-3">
            <div>
              <h2 className="font-serif text-base font-extrabold text-[#203f3c]">{bn ? "সাম্প্রতিক কার্যক্রম" : "Recent Activity"}</h2>
              <p className="mt-0.5 text-xs text-[#7b8986]">{bn ? "আপনার সাম্প্রতিক ওয়ালেট লেনদেন" : "Your recent wallet transactions"}</p>
            </div>
            {transactions.length === 0 ? (
              <Card className="rounded-[20px] border-[#e4eeea] bg-white shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-14 text-center">
                  <Wallet className="mb-3 h-10 w-10 text-[#8a9b97]" />
                  <p className="text-sm font-semibold text-[#647571]">{bn ? "কোনো লেনদেন নেই" : "No transactions yet"}</p>
                  <p className="mt-1 text-xs text-[#899692]">{bn ? "পরামর্শ গ্রহণ করলে এখানে দেখা যাবে" : "Earnings appear here when consultations are paid"}</p>
                </CardContent>
              </Card>
            ) : (
              transactions.map((tx, i) => (
                <motion.div key={tx.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card className="rounded-[20px] border-[#e4eeea] bg-white shadow-none">
                    <CardContent className="flex items-center gap-3 p-3.5 sm:p-4">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tx.type === "credit" ? "bg-[#e4f3ef] text-[#398b82]" : "bg-[#fcede9] text-[#c66b5c]"}`}>
                        {tx.type === "credit" ? <ArrowDownCircle className="h-5 w-5" /> : <ArrowUpCircle className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-[#2a4541]">{tx.description || (tx.type === "credit" ? (bn ? "পরামর্শ ফি জমা" : "Consultation fee credited") : (bn ? "উত্তোলন" : "Withdrawal"))}</p>
                        <p className="text-[10px] text-[#7b8986]">{new Date(tx.created_at).toLocaleString()}</p>
                      </div>
                      <p className={`shrink-0 text-sm font-extrabold ${tx.type === "credit" ? "text-[#2f8879]" : "text-[#c66b5c]"}`}>
                        {tx.type === "credit" ? "+" : "-"}৳{tx.amount.toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </motion.div>
        )}

        {tab === "withdraw" && (
          <motion.div key="withdraw" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            {withdrawDone ? (
              <Card className="rounded-[22px] border-[#e4eeea] bg-white shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-14 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#e4f3ef]">
                    <CheckCircle2 className="h-8 w-8 text-[#398b82]" />
                  </div>
                  <h3 className="mb-1 font-extrabold text-base text-[#203f3c]">{bn ? "অনুরোধ জমা হয়েছে!" : "Request Submitted!"}</h3>
                  <p className="mb-5 text-sm text-[#71817d]">{bn ? "আপনার উত্তোলন প্রক্রিয়াধীন। সাধারণত ১-৩ কার্যদিবসের মধ্যে সম্পন্ন হয়।" : "Your withdrawal is being processed. Usually completes within 1-3 business days."}</p>
                  <button onClick={() => { setWithdrawDone(false); setWithdrawAmount(""); setAccountNumber(""); setBankName(""); setWithdrawMethod(null); }}
                    className="rounded-full bg-[#237e76] px-6 py-2.5 text-sm font-bold text-white">
                    {bn ? "নতুন অনুরোধ" : "New Request"}
                  </button>
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-[22px] border-[#e4eeea] bg-white shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-bold text-[#203f3c]">
                    <ArrowUpCircle className="h-4 w-4 text-[#398b82]" />
                    {bn ? "ব্যালেন্স উত্তোলন করুন" : "Withdraw Balance"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {balance < 100 && (
                    <div className="flex items-start gap-2 rounded-xl border border-[#f0d6a8] bg-[#fff8e9] p-3">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#bd7c20]" />
                      <p className="text-xs font-medium text-[#9b691f]">{bn ? "উত্তোলনের জন্য সর্বনিম্ন ৳100 প্রয়োজন" : "Minimum ৳100 required to withdraw"}</p>
                    </div>
                  )}

                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-[#647571]">{bn ? "পরিমাণ (৳)" : "Amount (৳)"}</label>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={e => setWithdrawAmount(e.target.value)}
                      placeholder="0"
                      max={balance}
                      min={100}
                      className="w-full rounded-xl border border-[#dce9e4] bg-[#f8fbfa] px-4 py-3 text-sm font-medium text-[#203f3c] outline-none focus:border-[#62aaa0] focus:ring-0"
                    />
                    <p className="mt-1 text-[10px] text-[#899692]">{bn ? `সর্বোচ্চ: ৳${balance.toLocaleString()}` : `Max: ৳${balance.toLocaleString()}`}</p>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-bold text-[#647571]">{bn ? "উত্তোলন পদ্ধতি" : "Withdrawal Method"}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {(Object.entries(methodIcons) as [WithdrawMethod, typeof methodIcons[WithdrawMethod]][]).map(([key, m]) => (
                        <button key={key} type="button" onClick={() => setWithdrawMethod(key)}
                          className={`flex items-center gap-2.5 rounded-xl border-2 p-3 text-left transition-colors ${withdrawMethod === key ? "border-[#398b82] bg-[#eef8f5]" : "border-[#e2ece8] bg-white hover:border-[#9bc8c0]"}`}>
                          <div className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg ${m.bg}`}>
                            {key === "bkash" && <img src={bkashLogo} alt="bKash" className="h-7 w-7 object-contain" />}
                            {key === "nagad" && <img src={nagadLogo} alt="Nagad" className="h-7 w-7 object-contain" />}
                            {key === "rocket" && <img src={rocketLogo} alt="Rocket" className="h-7 w-7 object-contain" />}
                            {key === "bank" && <Building2 className={`h-4 w-4 ${m.color}`} />}
                          </div>
                          <span className="text-xs font-bold text-[#2a4541]">{bn ? m.labelBn : m.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {withdrawMethod && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold text-[#647571]">
                          {withdrawMethod === "bank" ? (bn ? "অ্যাকাউন্ট নম্বর" : "Account Number") : (bn ? "মোবাইল নম্বর" : "Mobile Number")}
                        </label>
                        <input
                          type="text"
                          value={accountNumber}
                          onChange={e => setAccountNumber(e.target.value)}
                          placeholder={withdrawMethod === "bank" ? "1234567890123456" : "01XXXXXXXXX"}
                          className="w-full rounded-xl border border-[#dce9e4] bg-[#f8fbfa] px-4 py-3 text-sm font-medium text-[#203f3c] outline-none focus:border-[#62aaa0] focus:ring-0"
                        />
                      </div>
                      {withdrawMethod === "bank" && (
                        <div>
                          <label className="mb-1.5 block text-xs font-bold text-[#647571]">{bn ? "ব্যাংকের নাম" : "Bank Name"}</label>
                          <input
                            type="text"
                            value={bankName}
                            onChange={e => setBankName(e.target.value)}
                            placeholder={bn ? "যেমন: Dutch Bangla Bank" : "e.g. Dutch Bangla Bank"}
                            className="w-full rounded-xl border border-[#dce9e4] bg-[#f8fbfa] px-4 py-3 text-sm font-medium text-[#203f3c] outline-none focus:border-[#62aaa0] focus:ring-0"
                          />
                        </div>
                      )}
                    </motion.div>
                  )}

                  <button
                    onClick={handleWithdraw}
                    disabled={withdrawing || !withdrawMethod || !accountNumber || !withdrawAmount || balance < 100}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#237e76] py-3.5 text-sm font-bold text-white disabled:opacity-30">
                    {withdrawing
                      ? <><Loader2 className="h-4 w-4 animate-spin" />{bn ? "প্রক্রিয়াকরণ..." : "Processing..."}</>
                      : <><Send className="h-4 w-4" />{bn ? "উত্তোলন অনুরোধ পাঠান" : "Submit Withdrawal Request"}</>}
                  </button>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}

        {tab === "history" && (
          <motion.div key="history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-3">
            <div>
              <h2 className="font-serif text-base font-extrabold text-[#203f3c]">{bn ? "উত্তোলনের ইতিহাস" : "Withdrawal History"}</h2>
              <p className="mt-0.5 text-xs text-[#7b8986]">{bn ? "আপনার সাম্প্রতিক উত্তোলন" : "Your recent withdrawals"}</p>
            </div>
            {withdrawals.length === 0 ? (
              <Card className="rounded-[20px] border-[#e4eeea] bg-white shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-14 text-center">
                  <ArrowUpCircle className="mb-3 h-10 w-10 text-[#8a9b97]" />
                  <p className="text-sm font-semibold text-[#647571]">{bn ? "কোনো উত্তোলন নেই" : "No withdrawals yet"}</p>
                </CardContent>
              </Card>
            ) : (
              withdrawals.map((wd, i) => (
                <motion.div key={wd.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card className="rounded-[20px] border-[#e4eeea] bg-white shadow-none">
                    <CardContent className="flex items-center gap-3 p-3.5 sm:p-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e4f3ef] text-[#398b82]">
                        {wd.method === "bank" ? <Building2 className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-[#2a4541]">{methodIcons[wd.method as WithdrawMethod]?.label ?? wd.method}</p>
                        <p className="text-[10px] text-[#7b8986]">{new Date(wd.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-extrabold text-[#c66b5c]">-৳{wd.amount.toLocaleString()}</p>
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                          wd.status === "completed" ? "bg-[#e4f3ef] text-[#398b82]"
                            : wd.status === "rejected" ? "bg-[#fcede9] text-[#c66b5c]"
                            : "bg-[#fff3d8] text-[#a8731f]"
                        }`}>
                          {wd.status === "completed" ? (bn ? "সম্পন্ন" : "Completed")
                            : wd.status === "rejected" ? (bn ? "প্রত্যাখ্যাত" : "Rejected")
                            : (bn ? "প্রক্রিয়াধীন" : "Pending")}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WalletPage;
